import React from "react";

import { useCSSVariables } from "../hooks/use-css-variables";

const PARTS_COUNT = 18;
const TOTAL_TILT_DEGREE = 133;

interface Props {
  percent: number;
  disabled?: boolean;
}

export const Gauge = ({ percent, disabled }: Props) => {
  const completed = Math.ceil((PARTS_COUNT / 100) * percent);
  const [currentRotation, setCurrentRotation] = React.useState(0);
  const targetRotation =
    ((TOTAL_TILT_DEGREE * 2) / 100) * percent - TOTAL_TILT_DEGREE;

  React.useEffect(() => {
    let animationFrameId: number;
    const startTime = performance.now();
    const duration = 300; // 300ms animation
    const startRotation = currentRotation;
    const rotationDiff = targetRotation - startRotation;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic function
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const newRotation = startRotation + rotationDiff * easeOut;

      setCurrentRotation(newRotation);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [targetRotation]);

  const colors = useCSSVariables({
    borderPrimary: "--v2-border-primary",
    bgSecondary: "--v2-bg-secondary",
    bgTertiary: "--v2-action-tertiary",
    bgShadow: "--v2-overlay-shadow-100",
    step18: "--v2-profitability-1800",
    step17: "--v2-profitability-1700",
    step16: "--v2-profitability-1600",
    step15: "--v2-profitability-1500",
    step14: "--v2-profitability-1400",
    step13: "--v2-profitability-1300",
    step12: "--v2-profitability-1200",
    step11: "--v2-profitability-1100",
    step10: "--v2-profitability-1000",
    step9: "--v2-profitability-900",
    step8: "--v2-profitability-800",
    step7: "--v2-profitability-700",
    step6: "--v2-profitability-600",
    step5: "--v2-profitability-500",
    step4: "--v2-profitability-400",
    step3: "--v2-profitability-300",
    step2: "--v2-profitability-200",
    step1: "--v2-profitability-100",
  });

  const emptyFillColor = disabled ? colors.bgSecondary : colors.bgTertiary; // f2f is tertiary f9f is --v2-bg-secondary
  const completedFillColor =
    percent < 6
      ? colors.step18
      : percent < 11
      ? colors.step17
      : percent < 17
      ? colors.step16
      : percent < 22
      ? colors.step15
      : percent < 28
      ? colors.step14
      : percent < 33
      ? colors.step13
      : percent < 39
      ? colors.step12
      : percent < 44
      ? colors.step11
      : percent < 50
      ? colors.step10
      : percent < 55
      ? colors.step9
      : percent < 61
      ? colors.step8
      : percent < 66
      ? colors.step7
      : percent < 72
      ? colors.step6
      : percent < 77
      ? colors.step5
      : percent < 83
      ? colors.step4
      : percent < 88
      ? colors.step3
      : percent < 94
      ? colors.step2
      : colors.step1;

  return (
    <svg
      width="100%"
      height="193"
      viewBox="0 0 245 193"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="123" cy="96.5308" r="12" fill={emptyFillColor} />
      <circle cx="123" cy="96.5308" r="6" fill={colors.bgShadow} />
      <g transform="translate(117, 41)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d={
            "M0.0473328 55.2428C0.0161133 55.4908 0 55.7435 0 56C0 59.3137 2.68628 62 6 " +
            "62C9.31372 62 12 59.3137 12 56C12 55.7435 11.9839 55.4908 11.9527 55.2428L6 " +
            "0L0.0473328 55.2428ZM6 60C8.20914 60 10 58.2091 10 56C10 53.7909 8.20914 52 " +
            "6 52C3.79086 52 2 53.7909 2 56C2 58.2091 3.79086 60 6 60Z"
          }
          fill={colors.borderPrimary}
          transform={`rotate(${currentRotation})`}
          style={{ transformOrigin: "6px 56px" }}
        />
      </g>
      <circle cx="79.8665" cy="139.664" r="3" fill={emptyFillColor} />
      <circle cx="70.1724" cy="127.031" r="3" fill={emptyFillColor} />
      <circle cx="64.0786" cy="112.319" r="3" fill={emptyFillColor} />
      <circle cx="62" cy="96.5308" r="3" fill={emptyFillColor} />
      <circle cx="64.0785" cy="80.7428" r="3" fill={emptyFillColor} />
      <circle cx="70.1724" cy="66.0308" r="3" fill={emptyFillColor} />
      <circle cx="79.8665" cy="53.3972" r="3" fill={emptyFillColor} />
      <circle cx="92.4999" cy="43.7032" r="3" fill={emptyFillColor} />
      <circle cx="107.212" cy="37.6094" r="3" fill={emptyFillColor} />
      <circle cx="123" cy="35.5308" r="3" fill={emptyFillColor} />
      <circle cx="138.788" cy="37.6093" r="3" fill={emptyFillColor} />
      <circle cx="153.5" cy="43.7032" r="3" fill={emptyFillColor} />
      <circle cx="166.134" cy="53.3973" r="3" fill={emptyFillColor} />
      <circle cx="175.828" cy="66.0307" r="3" fill={emptyFillColor} />
      <circle cx="181.921" cy="80.7428" r="3" fill={emptyFillColor} />
      <circle cx="184" cy="96.5308" r="3" fill={emptyFillColor} />
      <circle cx="181.921" cy="112.319" r="3" fill={emptyFillColor} />
      <circle cx="175.828" cy="127.031" r="3" fill={emptyFillColor} />
      <circle cx="166.133" cy="139.664" r="3" fill={emptyFillColor} />
      <path
        d={
          "M69.3591 144.583C70.0863 145.395 70.0658 146.639 69.2884 147.403L55.0091 " +
          "161.431C54.2133 162.213 52.9301 162.194 52.1768 161.371C48.3955 157.241 44.9965 " +
          "152.803 42.0096 148.114C41.4105 147.173 41.7245 145.929 42.6853 145.363L59.9313 " +
          "135.202C60.8708 134.648 62.0782 134.951 62.6738 135.864C64.6708 138.927 66.9042 " +
          "141.843 69.3591 144.583Z"
        }
        fill={completed > 0 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M52.0563 117.514C53.1076 117.222 54.1959 117.828 54.5343 118.865C55.6735 122.358 " +
          "57.0761 125.751 58.727 129.017C59.2185 129.989 58.8765 131.185 57.9283 " +
          "131.721L40.5043 141.576C39.5329 142.126 38.2978 141.775 37.7835 140.784C35.2179 " +
          "135.844 33.0812 130.679 31.4036 125.346C31.0691 124.283 31.6943 123.164 32.7685 " +
          "122.866L52.0563 117.514Z"
        }
        fill={completed > 1 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M49.0405 98.3954C50.1319 98.3857 51.0266 99.2531 51.0844 100.343C51.2659 " +
          "103.768 51.6927 107.189 52.3672 110.58C52.4103 110.797 52.4543 111.013 52.4993 " +
          "111.229C52.7215 112.295 52.0819 113.36 51.0278 113.632L31.6462 118.642C30.5653 " +
          "118.922 29.4628 118.263 29.2228 117.172C29.0832 116.538 28.9499 115.902 28.823 " +
          "115.264C27.858 110.412 27.2734 105.515 27.0648 100.615C27.0174 99.5013 27.9109 " +
          "98.5824 29.0253 98.5725L49.0405 98.3954Z"
        }
        fill={completed > 2 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M51.0848 79.1579C52.1418 79.4312 52.7815 80.5012 52.5547 81.5691C51.7936 85.153 " +
          "51.3075 88.7876 51.0997 92.4408C51.0379 93.5274 50.1446 94.3901 49.0562 " +
          "94.3804L29.0379 94.2033C27.9212 94.1934 27.0268 93.2708 27.0777 92.1552C27.3315 " +
          "86.5918 28.069 81.0567 29.2839 75.6134C29.5266 74.5259 30.6273 73.8699 31.706 " +
          "74.1488L51.0848 79.1579Z"
        }
        fill={completed > 3 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M58.041 61.1185C58.9915 61.6561 59.3324 62.8555 58.8365 63.8285C57.9812 65.5067 " +
          "57.1899 67.2221 56.4654 68.9712C55.7871 70.6088 55.1711 72.2667 54.6178 " +
          "73.9417C54.2765 74.9748 53.1907 75.5764 52.1423 75.2855L32.8516 69.9332C31.7752 " +
          "69.6345 31.1501 68.5114 31.4884 67.4469C32.3102 64.8613 33.2433 62.3048 34.2872 " +
          "59.7847C35.3796 57.1474 36.5859 54.5676 37.9008 52.0519C38.4168 51.0646 39.6496 " +
          "50.7161 40.6192 51.2645L58.041 61.1185Z"
        }
        fill={completed > 4 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M69.4247 45.5071C70.2038 46.2725 70.2226 47.5194 69.4916 48.3308C67.0475 51.0441 " +
          "64.8119 53.941 62.8049 56.9967C62.2078 57.9059 61.0035 58.2058 60.0662 " +
          "57.6536L42.8179 47.4907C41.8554 46.9235 41.5423 45.6767 42.1449 44.7359C45.1488 " +
          "40.0468 48.5531 35.6307 52.3175 31.5367C53.0715 30.7168 54.3524 30.6992 55.147 " +
          "31.4799L69.4247 45.5071Z"
        }
        fill={completed > 5 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M84.4508 33.3829C85.0052 34.3237 84.7007 35.5329 83.7847 36.1276C83.5187 36.3003 " +
          "83.2537 36.4748 82.9897 36.6512C80.2083 38.5097 77.571 40.5521 75.0928 " +
          "42.76C74.2807 43.4836 73.0399 43.4615 72.2776 42.6855L58.2473 28.4047C57.4643 " +
          "27.6077 57.4847 26.3223 58.3103 25.5696C61.849 22.3436 65.6384 19.3738 69.653 " +
          "16.6914C70.2784 16.2735 70.9082 15.8633 71.5421 15.4609C72.4826 14.8639 73.7245 " +
          "15.1784 74.2901 16.1382L84.4508 33.3829Z"
        }
        fill={completed > 6 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M102.089 25.5634C102.381 26.6155 101.774 27.7045 100.736 28.0421C97.2752 29.1671 " +
          "93.8983 30.5549 90.6358 32.1961C89.664 32.685 88.4711 32.3425 87.9355 " +
          "31.3956L78.0795 13.9702C77.5295 12.9978 77.8818 11.7614 78.874 11.248C83.8193 " +
          "8.68931 88.9676 6.57088 94.2587 4.91107C95.3217 4.57758 96.4402 5.20282 " +
          "96.7381 6.2764L102.089 25.5634Z"
        }
        fill={completed > 7 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M120.961 2.55598C120.951 1.44162 120.032 0.548128 118.919 0.595485C113.351 " +
          "0.832303 107.835 1.55249 102.43 2.73789C101.339 2.97719 100.679 4.08009 " +
          "100.958 5.16154L105.968 24.5439C106.241 25.5974 107.305 26.2369 108.37 " +
          "26.0159C111.925 25.2783 115.542 24.8083 119.19 24.6151C120.28 24.5573 " +
          "121.148 23.6626 121.138 22.5712L120.961 2.55598Z"
        }
        fill={completed > 8 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M140.303 24.6007C140.03 25.657 138.961 26.2967 137.893 26.0711C134.344 25.3207 " +
          "130.732 24.8374 127.087 24.6303C126.001 24.5685 125.138 23.6752 125.148 " +
          "22.5869L125.325 2.56856C125.335 1.45188 126.257 0.557483 127.373 0.60833C132.936 " +
          "0.861909 138.447 1.59812 143.846 2.79883C144.934 3.04084 145.591 4.14199 145.312 " +
          "5.22125L140.303 24.6007Z"
        }
        fill={completed > 9 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M158.287 31.5079C157.75 32.4571 156.553 32.7986 155.58 32.3052C152.331 30.6568 " +
          "148.967 29.2605 145.518 28.1254C144.484 27.7849 143.881 26.6984 144.173 " +
          "25.6491L149.525 6.35926C149.823 5.2835 150.945 4.65836 152.009 4.99563C157.287 " +
          "6.6681 162.421 8.79732 167.351 11.3651C168.34 11.8801 168.69 13.1142 " +
          "168.141 14.0849L158.287 31.5079Z"
        }
        fill={completed > 10 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M173.875 42.8215C173.111 43.599 171.867 43.6195 171.055 42.8921C168.534 " +
          "40.6336 165.847 38.5467 163.01 36.6512C162.813 36.5192 162.615 36.3883 162.416 " +
          "36.2584C161.504 35.6621 161.203 34.456 161.756 33.5174L171.918 16.2705C172.484 " +
          "15.309 173.729 14.9954 174.67 15.5959C175.232 15.955 175.791 16.3201 176.347 " +
          "16.6913C180.42 19.4127 184.261 22.4298 187.843 25.71C188.666 26.4634 " +
          "188.685 27.7465 187.903 28.5423L173.875 42.8215Z"
        }
        fill={completed > 11 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M186.013 57.775C185.075 58.328 183.869 58.0265 183.272 57.1147C181.281 54.07 " +
          "179.063 51.1822 176.639 48.4757C175.911 47.6639 175.932 46.4201 176.709 " +
          "45.6562L190.988 31.6275C191.784 30.8457 193.067 30.8646 193.821 31.6874C197.564 " +
          "35.7754 200.948 40.1827 203.935 44.8608C204.535 45.8014 204.222 47.0464 203.26 " +
          "47.613L186.013 57.775Z"
        }
        fill={completed > 12 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M193.882 75.3581C192.832 75.6493 191.746 75.0465 191.405 74.0123C190.846 72.3133 " +
          "190.222 70.6318 189.535 68.9712C188.828 67.2644 188.057 65.5898 187.225 " +
          "63.9505C186.732 62.9779 187.074 61.7809 188.023 61.244L205.446 51.3893C206.416 " +
          "50.8403 207.651 51.1902 208.166 52.1793C209.455 54.6547 210.639 57.1919 211.713 " +
          "59.7847C212.767 62.329 213.708 64.9103 214.535 67.5213C214.872 68.5855 214.247 " +
          "69.7076 213.172 70.006L193.882 75.3581Z"
        }
        fill={completed > 13 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M196.944 94.383C195.855 94.3926 194.962 93.5299 194.9 92.4433C194.694 88.8124 " +
          "194.213 85.1999 193.46 81.6372C193.234 80.5698 193.874 79.501 194.93 " +
          "79.228L214.309 74.2186C215.389 73.9397 216.49 74.5965 216.732 75.6846C217.938 " +
          "81.1059 218.67 86.6178 218.922 92.1578C218.973 93.2733 218.079 94.1959 216.962 " +
          "94.2058L196.944 94.383Z"
        }
        fill={completed > 14 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M194.987 113.562C193.933 113.29 193.294 112.226 193.515 111.161C193.555 110.968 " +
          "193.594 110.774 193.633 110.58C194.307 107.189 194.734 103.767 194.916 " +
          "100.341C194.973 99.2507 195.868 98.3832 196.96 98.3929L216.975 98.57C218.089 " +
          "98.5798 218.983 99.4987 218.935 100.612C218.727 105.513 218.142 110.412 217.177 " +
          "115.264C217.055 115.878 216.927 116.49 216.793 117.101C216.554 118.192 215.451 " +
          "118.852 214.369 118.573L194.987 113.562Z"
        }
        fill={completed > 15 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M188.135 131.595C187.188 131.06 186.846 129.867 187.335 128.895C188.97 125.644 " +
          "190.36 122.268 191.489 118.795C191.826 117.756 192.915 117.149 193.967 " +
          "117.441L213.254 122.793C214.328 123.091 214.953 124.209 214.62 125.272C212.953 " +
          "130.585 210.831 135.732 208.283 140.657C207.769 141.649 206.533 142.001 " +
          "205.56 141.451L188.135 131.595Z"
        }
        fill={completed > 16 ? completedFillColor : emptyFillColor}
      />
      <path
        d={
          "M176.845 147.253C176.069 146.491 176.047 145.25 176.771 144.438C179.206 141.705 " +
          "181.422 138.798 183.403 135.746C183.998 134.83 185.207 134.526 186.148 " +
          "135.08L203.393 145.241C204.352 145.806 204.667 147.048 204.07 147.989C201.1 " +
          "152.667 197.721 157.096 193.961 161.22C193.208 162.046 191.923 162.066 191.126 " +
          "161.283L176.845 147.253Z"
        }
        fill={completed > 17 ? completedFillColor : emptyFillColor}
      />
    </svg>
  );
};
